import React, { FC } from 'react'
import _ from 'lodash'
import { useMutation } from '@apollo/client/react'

import {
  Caption,
  Grid,
  Tooltip,
  Button,
  List,
  ProgressBar,
} from '@design-systems/canvas-ui'

import {
  triggerDownload,
  navigateToNotificationUrl,
  getNotificationMessageByStatus,
} from '../utils/backgroundNotificationsHelper'
import { backgroundNotificationsConfig } from '../utils/backgroundNotificationsConfig'
import Icon, {
  ArrowRightIcon,
  CloseIcon,
  ClipboardIcon,
  DownloadIcon,
  FillBackstockIcon,
  WarningIcon,
} from '@design-systems/react-icons'
import { useNavigate } from 'react-router-dom'
import { UPDATE_BACKGROUND_NOTIFICATION } from 'graphql/mutations/backgroundActivity/backgroundActivityMutations'
import type { BackgroundActivityNotification } from '../types/BackgroundActivityTypes'

export enum BACKGROUND_NOTIFICATIONS_STATUS {
  INPROGRESS = 'INPROGRESS',
  ERROR = 'ERROR',
  COMPLETED = 'COMPLETED',
}
export enum BACKGROUND_NOTIFICATIONS_ACTIONS {
  REDIRECT = 'REDIRECT',
  DOWNLOAD = 'DOWNLOAD',
}

export enum CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE {
  ARCHIVE = 'ARCHIVE',
  NAVIGATE = 'NAVIGATE',
  DOWNLOAD = 'DOWNLOAD',
  RETRY = 'RETRY',
}

export enum BACKGROUND_NOTIFICATIONS_CATEGORY {
  CREATE = 'CREATE',
  REQUEST = 'REQUEST',
  EXPORT = 'EXPORT',
  UPLOAD = 'UPLOAD',
  RELEASE = 'RELEASE',
}

export enum BACKGROUND_NOTIFICATIONS_TYPE {
  TECHPACK = 'TECHPACK',
  RELEASEPACK = 'RELEASE_PACKET',
  INSIGHTSEXPORT = 'INSIGHTS_EXPORT',
}

// TODO: flip to true once the federated schema exposes retryPdfGeneration.
// Everything else in the retry flow (enum value, retry() handler, icon +
// tooltip mappings, switch cases) is left intact so this is a one-line re-enable.
const RETRY_ENABLED = false

interface Props {
  notification: BackgroundActivityNotification
}

export const BackgroundNotificationItem: FC<Props> = (props): any => {
  const { notification } = props

  const navigate = useNavigate()
  const [updateBackgroundNotification] = useMutation(
    UPDATE_BACKGROUND_NOTIFICATION,
  )
  const getNotificationTypeIcon = (
    notification: BackgroundActivityNotification,
  ) => {
    const { category, status } = notification

    let icon
    if (status === BACKGROUND_NOTIFICATIONS_STATUS.ERROR) {
      icon = WarningIcon
    } else {
      const categoryConfig = backgroundNotificationsConfig.categories.find(
        (categoryConfig) => categoryConfig.name === category,
      )
      const defaultCategoryConfig =
        backgroundNotificationsConfig.categories.find(
          (categoryConfig) =>
            categoryConfig.name === BACKGROUND_NOTIFICATIONS_CATEGORY.REQUEST,
        )
      const defaultIcon = defaultCategoryConfig
        ? defaultCategoryConfig.icon
        : ClipboardIcon

      icon = categoryConfig ? categoryConfig.icon : defaultIcon
    }

    return icon
  }

  const getNotificationActionIcon = (
    actionType: CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE,
  ) => {
    let icon
    switch (actionType) {
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.ARCHIVE:
        icon = CloseIcon
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.RETRY:
        icon = FillBackstockIcon
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.NAVIGATE:
        icon = ArrowRightIcon
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.DOWNLOAD:
        icon = DownloadIcon
        break
    }
    return icon
  }

  const retry = async (notification: BackgroundActivityNotification) => {
    if (
      notification.type === BACKGROUND_NOTIFICATIONS_TYPE.TECHPACK ||
      notification.type === BACKGROUND_NOTIFICATIONS_TYPE.RELEASEPACK
    ) {
      // this query isn't in federated yet, and honestly not sure how this flow worked, so for now. commenting this out and we can revisit when we have a better understanding of the flow and how to implement it in federated.
      // await retryPdfGeneration({
      //   variables: {
      //     reportId: notification.originatorId,
      //   },
      // })
    }
  }

  const archive = async (notification: BackgroundActivityNotification) => {
    await updateBackgroundNotification({
      variables: {
        notificationBulkUpdate: {
          notificationUpdates: [
            {
              id: notification.id,
              archived: true,
              visited: true,
            },
          ],
        },
      },
    })
  }

  const handleNotificationActionClick = (
    notificationActionType: CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE,
    notification: BackgroundActivityNotification,
    navigate: any,
  ) => {
    switch (notificationActionType) {
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.ARCHIVE:
        archive(notification)
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.NAVIGATE:
        navigateToNotificationUrl(notification, navigate)
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.DOWNLOAD:
        triggerDownload(notification)
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.RETRY:
        retry(notification)
        break
    }
  }

  const getCaptionBelowSuffix = (
    notification: BackgroundActivityNotification,
  ) => {
    let captionBelowSuffix = ''
    if (
      notification.status === BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED &&
      (notification.action === BACKGROUND_NOTIFICATIONS_ACTIONS.DOWNLOAD ||
        notification.action === BACKGROUND_NOTIFICATIONS_ACTIONS.REDIRECT)
    ) {
      captionBelowSuffix = ` - Ready to ${notification.action === BACKGROUND_NOTIFICATIONS_ACTIONS.DOWNLOAD ? 'Download' : 'View'}`
    }
    return captionBelowSuffix
  }

  const getCustomNotificationActionTypes = (
    notification: BackgroundActivityNotification,
  ) => {
    const { action, status } = notification
    let actionTypes
    switch (status) {
      case BACKGROUND_NOTIFICATIONS_STATUS.INPROGRESS:
        actionTypes = [CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.ARCHIVE]
        break
      case BACKGROUND_NOTIFICATIONS_STATUS.ERROR:
        actionTypes = [
          ...(RETRY_ENABLED
            ? [CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.RETRY]
            : []),
          CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.ARCHIVE,
        ]
        break
      case BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED:
        switch (action) {
          case BACKGROUND_NOTIFICATIONS_ACTIONS.REDIRECT:
            actionTypes = [CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.NAVIGATE]
            break
          case BACKGROUND_NOTIFICATIONS_ACTIONS.DOWNLOAD:
            actionTypes = [CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.DOWNLOAD]
            break
        }
        break
    }

    return actionTypes
  }

  const getTooltipContent = (
    actionType: CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE,
    notification: BackgroundActivityNotification,
  ) => {
    let content = ''

    switch (actionType) {
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.ARCHIVE:
        content = `Clear from list`
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.NAVIGATE:
        content = `Navigate to ${_.capitalize(notification.type)}`
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.DOWNLOAD:
        content = `Download files`
        break
      case CUSTOM_BACKGROUND_NOTIFICATIONS_ACTION_TYPE.RETRY:
        content = `Retry`
        break
    }

    return content
  }

  const notificationMessage = getNotificationMessageByStatus(
    notification,
    notification.status,
  )
  const captionBelowSuffix = getCaptionBelowSuffix(notification)
  const notificationActionTypes = getCustomNotificationActionTypes(notification)

  return (
    <List.Item
      isSeparated
      className={
        notification.status === BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED
          ? 'hc-pb-xs hc-pt-xs'
          : 'hc-pb-none hc-pt-xs'
      }
    >
      <Grid.Container spacing="dense" justify="space-between">
        <Grid.Item className="hc8-pb-none">
          <div
            className={
              notification.status === BACKGROUND_NOTIFICATIONS_STATUS.ERROR
                ? 'backgroundActivity_listItem_error_background'
                : 'spark-BackgroundActivity__ListItemIcon__Background'
            }
          >
            <span data-icon>
              <Icon size="lg" svg={getNotificationTypeIcon(notification)} />
            </span>
          </div>
        </Grid.Item>
        <Grid.Item
          className="hc8-ws-no-wrap hc8-to-ellipsis hc8-ov-hidden hc8-mt-1x"
          size={{
            xs: true,
          }}
        >
          <Caption
            below={
              <span
                className={
                  notification.status === BACKGROUND_NOTIFICATIONS_STATUS.ERROR
                    ? 'hc-clr-error'
                    : ''
                }
              >
                {notificationMessage?.title}
                {captionBelowSuffix}
              </span>
            }
            aria-label={`${notification.type}`}
            data-test={`notification-item-${notification.id}`}
            className="hc8-ws-no-wrap hc8-to-ellipsis hc8-ov-hidden hc8-lh-display"
          >
            {notification.description}
          </Caption>
        </Grid.Item>
        <Grid.Item>
          <Grid.Container spacing="none" align="start-end">
            {notificationActionTypes?.map((notificationActionType) => (
              <Grid.Item key={`${notification.id}-${notificationActionType}`}>
                <Tooltip
                  location="left"
                  content={getTooltipContent(
                    notificationActionType,
                    notification,
                  )}
                >
                  <Button
                    isIconOnly
                    data-test={`backgroundActivityAction-${notification.id}-${notificationActionType}`}
                    aria-label="background action"
                    onClick={() =>
                      handleNotificationActionClick(
                        notificationActionType,
                        notification,
                        navigate,
                      )
                    }
                  >
                    <Icon
                      svg={getNotificationActionIcon(notificationActionType)}
                    />
                  </Button>
                </Tooltip>
              </Grid.Item>
            ))}
          </Grid.Container>
        </Grid.Item>
      </Grid.Container>
      {notification.status !== BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED && (
        <Grid.Container className="hc8-pt-1x">
          <Grid.Item
            size={{
              xs: 12,
            }}
          >
            <ProgressBar
              className="hc8-mb-none hc8-mt-none"
              {...(notification.status === BACKGROUND_NOTIFICATIONS_STATUS.ERROR
                ? { error: true }
                : { success: true })}
              percentComplete={notification.progressPercentage}
            />
          </Grid.Item>
        </Grid.Container>
      )}
    </List.Item>
  )
}

BackgroundNotificationItem.displayName = 'BackgroundNotificationItem'
